/**
 * Data Source Service
 *
 * Handles communication with the backend for external data sources.
 * Used to populate widgets with data from CSV files or other external sources.
 */

import api from './api';

// Types
export interface DataSourceInfo {
  id: string;
  name: string;
  description: string;
  fileName: string;
  columns: string[];
  rowCount: number;
}

export interface ColumnSchema {
  name: string;
  dtype: 'string' | 'number' | 'integer' | 'boolean' | 'date' | 'datetime';
  nullable: boolean;
  sampleValues: (string | number | boolean)[];
}

export interface DataSourceSchema {
  id: string;
  name: string;
  description: string;
  columns: ColumnSchema[];
  rowCount: number;
}

export interface DataProviderInfo {
  id: string;
  name: string;
  description: string;
  version: string;
  availableSources: number;
  sourceIds: string[];
}

export interface DataSourceData {
  sourceId: string;
  columns: string[];
  data: Record<string, unknown>[];
  totalRows: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ChartDataset {
  label: string;
  data: number[];
  backgroundColor: string;
  borderColor: string;
}

export interface ChartData {
  sourceId: string;
  chartType: string;
  labels: string[];
  datasets: ChartDataset[];
}

export interface AggregateData {
  sourceId: string;
  column: string;
  aggregation: string;
  value: number | string;
}

export interface UniqueValuesData {
  sourceId: string;
  column: string;
  uniqueValues: (string | number)[];
  totalUnique: number;
}

export interface SecuritySearchResult {
  uid: string;
  name: string;
  description: string;
  type: string;
  sector: string;
}

export interface DataSourceQueryParams {
  page?: number;
  pageSize?: number;
  columns?: string[];
  sortBy?: string;
  sortDesc?: boolean;
  filterColumn?: string;
  filterValue?: string;
  filterOp?: 'eq' | 'ne' | 'gt' | 'lt' | 'gte' | 'lte' | 'contains';
}

export interface ChartQueryParams {
  chartType?: 'bar' | 'line';
  xColumn: string;
  yColumns: string[];
  aggregation?: 'none' | 'sum' | 'avg' | 'count' | 'min' | 'max';
  groupBy?: string;
  limit?: number;
  filterColumn?: string;      // Column to filter on (e.g., UID)
  filterValue?: string;       // Value to filter by (e.g., 'BKR')
  filterOp?: 'eq' | 'ne' | 'gt' | 'lt' | 'gte' | 'lte' | 'contains';
}

export interface AggregateQueryParams {
  column: string;
  aggregation?: 'sum' | 'avg' | 'count' | 'min' | 'max' | 'median';
  filterColumn?: string;
  filterValue?: string;
  filterOp?: string;
}

// Visualization Schema Types
export interface VisualizationConfig {
  type: 'chart' | 'grid';
  chartType?: 'bar' | 'line';
  xColumn?: string;
  yColumns?: string[];
  groupBy?: string;
  aggregation?: 'none' | 'sum' | 'avg' | 'count' | 'min' | 'max';
  title?: string;
  columns?: string[];  // For grid type
}

export interface AssetBinding {
  column: string;        // The column in the data source that maps to asset's data_uid
  description?: string;  // Human-readable description of what this binding does
  defaultValue?: string; // Default UID to use for preview/design time
}

export interface ColumnMetadata {
  type: 'string' | 'number' | 'integer' | 'boolean' | 'date' | 'datetime';
  format?: string;
  label: string;
  role: 'dimension' | 'measure' | 'category' | 'identifier';
}

export interface VisualizationSchema {
  name: string;
  description: string;
  assetBinding?: AssetBinding;  // How to filter data by asset
  visualization: VisualizationConfig;
  alternateVisualization?: VisualizationConfig;
  columns: Record<string, ColumnMetadata>;
  defaults?: {
    sortBy?: string;
    sortDesc?: boolean;
    limit?: number;
    pageSize?: number;
  };
}

export interface DataSourceWithSchema {
  id: string;
  name: string;
  description: string;
  fileName: string;
  columns: string[];
  rowCount: number;
  hasSchema: boolean;
  assetBinding: AssetBinding | null;
  visualization: VisualizationConfig | null;
  columnMetadata: Record<string, ColumnMetadata> | null;
  defaults: VisualizationSchema['defaults'] | null;
}

// Service functions
export const dataSourceService = {
  /**
   * Check if the data provider service is healthy
   */
  async checkHealth(): Promise<{ status: string; provider: unknown }> {
    const response = await api.get('/datasources/health');
    return response.data;
  },

  /**
   * Get information about the data provider
   */
  async getProviderInfo(): Promise<DataProviderInfo> {
    const response = await api.get('/datasources/provider');
    return {
      id: response.data.id,
      name: response.data.name,
      description: response.data.description,
      version: response.data.version,
      availableSources: response.data.available_sources,
      sourceIds: response.data.source_ids,
    };
  },

  /**
   * List all available data sources
   */
  async listSources(): Promise<DataSourceInfo[]> {
    const response = await api.get('/datasources/sources');
    // Backend returns { sources: [...] }, extract the array and convert snake_case to camelCase
    const sources = response.data.sources || [];
    return sources.map((source: { id: string; name: string; description: string; file_name: string; columns: string[]; row_count: number }) => ({
      id: source.id,
      name: source.name,
      description: source.description,
      fileName: source.file_name,
      columns: source.columns,
      rowCount: source.row_count,
    }));
  },

  /**
   * Get information about a specific data source
   */
  async getSourceInfo(sourceId: string): Promise<DataSourceInfo> {
    const response = await api.get(`/datasources/sources/${sourceId}`);
    // Convert snake_case to camelCase
    return {
      id: response.data.id,
      name: response.data.name,
      description: response.data.description,
      fileName: response.data.file_name,
      columns: response.data.columns,
      rowCount: response.data.row_count,
    };
  },

  /**
   * Get detailed schema information about a data source including column types
   */
  async getSourceSchema(sourceId: string): Promise<DataSourceSchema> {
    const response = await api.get(`/datasources/sources/${sourceId}/schema`);

    // Convert snake_case to camelCase
    return {
      id: response.data.id,
      name: response.data.name,
      description: response.data.description,
      rowCount: response.data.row_count,
      columns: response.data.columns.map((col: { name: string; dtype: string; nullable: boolean; sample_values: unknown[] }) => ({
        name: col.name,
        dtype: col.dtype,
        nullable: col.nullable,
        sampleValues: col.sample_values,
      })),
    };
  },

  /**
   * Get paginated data from a source with optional filtering and sorting
   */
  async getData(sourceId: string, params: DataSourceQueryParams = {}): Promise<DataSourceData> {
    const queryParams: Record<string, string | number | boolean> = {};

    if (params.page) queryParams.page = params.page;
    if (params.pageSize) queryParams.page_size = params.pageSize;
    if (params.columns?.length) queryParams.columns = params.columns.join(',');
    if (params.sortBy) queryParams.sort_by = params.sortBy;
    if (params.sortDesc !== undefined) queryParams.sort_desc = params.sortDesc;
    if (params.filterColumn) queryParams.filter_column = params.filterColumn;
    if (params.filterValue !== undefined) queryParams.filter_value = params.filterValue;
    if (params.filterOp) queryParams.filter_op = params.filterOp;

    const response = await api.get(`/datasources/sources/${sourceId}/data`, { params: queryParams });

    // Convert snake_case to camelCase
    return {
      sourceId: response.data.source_id,
      columns: response.data.columns,
      data: response.data.data,
      totalRows: response.data.total_rows,
      page: response.data.page,
      pageSize: response.data.page_size,
      totalPages: response.data.total_pages,
    };
  },

  /**
   * Get data formatted for charts
   */
  async getChartData(sourceId: string, params: ChartQueryParams): Promise<ChartData> {
    const queryParams: Record<string, string | number> = {
      x_column: params.xColumn,
      y_columns: params.yColumns.join(','),
    };

    if (params.chartType) queryParams.chart_type = params.chartType;
    if (params.aggregation) queryParams.aggregation = params.aggregation;
    if (params.groupBy) queryParams.group_by = params.groupBy;
    if (params.limit) queryParams.limit = params.limit;
    // Filter params for asset binding
    if (params.filterColumn) queryParams.filter_column = params.filterColumn;
    if (params.filterValue) queryParams.filter_value = params.filterValue;
    if (params.filterOp) queryParams.filter_op = params.filterOp;

    const response = await api.get(`/datasources/sources/${sourceId}/chart`, { params: queryParams });

    return {
      sourceId: response.data.source_id,
      chartType: response.data.chart_type,
      labels: response.data.labels,
      datasets: response.data.datasets,
    };
  },

  /**
   * Get a single aggregated value
   */
  async getAggregate(sourceId: string, params: AggregateQueryParams): Promise<AggregateData> {
    const queryParams: Record<string, string> = {
      column: params.column,
    };

    if (params.aggregation) queryParams.aggregation = params.aggregation;
    if (params.filterColumn) queryParams.filter_column = params.filterColumn;
    if (params.filterValue !== undefined) queryParams.filter_value = params.filterValue;
    if (params.filterOp) queryParams.filter_op = params.filterOp;

    const response = await api.get(`/datasources/sources/${sourceId}/aggregate`, { params: queryParams });

    return {
      sourceId: response.data.source_id,
      column: response.data.column,
      aggregation: response.data.aggregation,
      value: response.data.value,
    };
  },

  /**
   * Get unique values for a column (useful for filters)
   */
  async getUniqueValues(sourceId: string, column: string, limit: number = 100): Promise<UniqueValuesData> {
    const response = await api.get(`/datasources/sources/${sourceId}/columns/${column}/unique`, {
      params: { limit },
    });

    return {
      sourceId: response.data.source_id,
      column: response.data.column,
      uniqueValues: response.data.unique_values,
      totalUnique: response.data.total_unique,
    };
  },

  /**
   * Refresh cached data for a source
   */
  async refreshSource(sourceId: string): Promise<{ status: string; sourceId: string; rowCount: number }> {
    const response = await api.post(`/datasources/sources/${sourceId}/refresh`);
    return {
      status: response.data.status,
      sourceId: response.data.source_id,
      rowCount: response.data.row_count,
    };
  },

  /**
   * Get visualization schema for a data source (if available)
   */
  async getVisualizationSchema(sourceId: string): Promise<{ sourceId: string; hasSchema: boolean; schema: VisualizationSchema | null }> {
    const response = await api.get(`/datasources/sources/${sourceId}/visualization-schema`);
    return {
      sourceId: response.data.source_id,
      hasSchema: response.data.has_schema,
      schema: response.data.schema,
    };
  },

  /**
   * List all data sources with their visualization schemas
   */
  async listSourcesWithSchemas(): Promise<DataSourceWithSchema[]> {
    const response = await api.get('/datasources/sources-with-schemas');
    const sources = response.data || [];
    return sources.map((source: {
      id: string;
      name: string;
      description: string;
      file_name: string;
      columns: string[];
      row_count: number;
      has_schema: boolean;
      asset_binding: AssetBinding | null;
      visualization: VisualizationConfig | null;
      column_metadata: Record<string, ColumnMetadata> | null;
      defaults: VisualizationSchema['defaults'] | null;
    }) => ({
      id: source.id,
      name: source.name,
      description: source.description,
      fileName: source.file_name,
      columns: source.columns,
      rowCount: source.row_count,
      hasSchema: source.has_schema,
      assetBinding: source.asset_binding,
      visualization: source.visualization,
      columnMetadata: source.column_metadata,
      defaults: source.defaults,
    }));
  },

  /**
   * Search securities by name or symbol
   * Returns matching securities with uid, name, description, type, sector
   */
  async searchSecurities(query: string, limit: number = 10): Promise<SecuritySearchResult[]> {
    const response = await api.get('/datasources/securities/search', {
      params: { q: query, limit },
    });
    return response.data;
  },

  // ---------------------------------------------------------------------------
  // New widget pipeline (data-provider widget API)
  // ---------------------------------------------------------------------------

  /**
   * Get which widgets the current user is entitled to.
   * Backend resolves user identity and queries the data-provider.
   */
  async getAvailableWidgets(): Promise<{ user_id: string; widgets: string[]; providers: string[] }> {
    const response = await api.get('/datasources/available-widgets');
    return response.data;
  },

  /**
   * Get which data providers the current user has access to.
   */
  async getAvailableProviders(): Promise<{ user_id: string; providers: string[] }> {
    const response = await api.get('/datasources/available-providers');
    return response.data;
  },

  /**
   * Request structured widget data from the data-provider via the backend.
   *
   * Caching is handled at the data-provider level (per provider function call
   * + asset), not at the backend or frontend level.
   *
   * @param widgets - List of widget names, or a dict with per-widget params
   *   e.g. ["COMPANY_EARNINGS", "PRICE"]
   *   or   { "PRICE": { "horizon": 30 } }
   * @param params - Shared params passed to all widgets (e.g. { ticker: "AAPL" })
   *
   * @returns The data-provider response with fully structured widget objects:
   *   { user_id, widgets: { COMPANY_EARNINGS: {...}, PRICE: {...} }, errors?: {...} }
   */
  async fetchWidgets(
    widgets: string[] | Record<string, Record<string, unknown>>,
    params: Record<string, unknown> = {},
  ): Promise<{
    user_id: string;
    widgets: Record<string, unknown>;
    errors?: Record<string, string>;
  }> {
    const response = await api.post('/datasources/widgets', {
      widgets,
      params,
    });
    return response.data;
  },
};

export default dataSourceService;
